import React from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../App';
import { TrendingUp, Target, Award, BarChart3, ArrowRight, CheckCircle } from 'lucide-react';

export default function SkillDevelopmentPage() {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-green-950/50 to-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-600/20 border border-green-600/40 rounded-full mb-6">
            <TrendingUp className="w-4 h-4 text-green-400" />
            <span className="text-green-400 text-sm font-bold uppercase tracking-wider">Level Up</span>
          </div>

          <h1 className="text-5xl sm:text-6xl font-heading font-bold mb-6">
            Track Your Progress
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-10">
            From complete beginner to confident player, we guide you every step of the way with structured learning paths.
          </p>
        </div>
      </section>

      {/* Learning Path */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-heading font-bold text-center mb-16">
            Your Learning Journey
          </h2>

          <div className="space-y-8">
            {[
              {
                level: 'Beginner',
                color: 'blue',
                icon: <Target className="w-8 h-8" />,
                title: 'Foundation',
                skills: ['Hand Rankings', 'Basic Rules', 'Position Play', 'Starting Hands'],
                link: '/lessons'
              },
              {
                level: 'Intermediate',
                color: 'yellow',
                icon: <BarChart3 className="w-8 h-8" />,
                title: 'Strategy',
                skills: ['Pot Odds', 'Reading Opponents', 'Bet Sizing', 'Table Dynamics'],
                link: '/lessons'
              },
              {
                level: 'Advanced',
                color: 'green',
                icon: <Award className="w-8 h-8" />,
                title: 'Mastery',
                skills: ['Game Theory', 'Range Analysis', 'Tournament Play', 'Mental Game'],
                link: '/lessons'
              }
            ].map((stage, idx) => (
              <Link
                to={stage.link}
                key={idx}
                className="block bg-card border border-border rounded-2xl p-8 hover:border-primary/50 transition-all cursor-pointer"
                data-testid={`learning-path-card-${stage.level.toLowerCase()}`}
              >
                <div className="flex items-start gap-6">
                  <div className={`w-16 h-16 bg-${stage.color}-600/10 rounded-xl flex items-center justify-center flex-shrink-0 text-${stage.color}-500`}>
                    {stage.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-4">
                      <h3 className="text-2xl font-bold">{stage.title}</h3>
                      <span className={`px-3 py-1 bg-${stage.color}-600/20 text-${stage.color}-400 rounded-full text-sm font-semibold`}>
                        {stage.level}
                      </span>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      {stage.skills.map((skill, i) => (
                        <div key={i} className="flex items-center gap-2 text-muted-foreground">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>{skill}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-card/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-heading font-bold text-center mb-12">
            How We Help You Improve
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <Link to="/lessons" className="bg-card border border-border rounded-xl p-8 cursor-pointer hover:border-primary/50 transition-all" data-testid="feature-personalized-plans">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3">Personalized Plans</h3>
              <p className="text-muted-foreground">Custom learning paths based on your current skill level and goals.</p>
            </Link>

            <Link to="/dashboard" className="bg-card border border-border rounded-xl p-8 cursor-pointer hover:border-primary/50 transition-all" data-testid="feature-progress-tracking">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <BarChart3 className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3">Progress Tracking</h3>
              <p className="text-muted-foreground">See exactly how far you've come and what to focus on next.</p>
            </Link>

            <Link to="/community" className="bg-card border border-border rounded-xl p-8 cursor-pointer hover:border-primary/50 transition-all" data-testid="feature-expert-feedback">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <Award className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3">Expert Feedback</h3>
              <p className="text-muted-foreground">Get personalized advice from pros on your specific hands and situations.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-heading font-bold mb-6">
            Start Your Journey Today
          </h2>
          <p className="text-xl text-muted-foreground mb-10">
            Join our community and watch your poker skills soar.
          </p>
          <Link
            to="/membership"
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-black px-10 py-5 rounded-xl text-lg font-bold shadow-lg transition-all"
          >
            Get Started
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
